import type { MaybeRefOrGetter } from '@vueuse/core';
import { useQuery } from '@tanstack/vue-query';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { minutesToMilliseconds } from 'date-fns';

import { fetchFinishRequirementsStatus, fetchRequirement, fetchRequirementSteps } from '../services/requirement';
import { RequirementType } from '../schemas/requirement';

type UseFetchRequirementOptions = {
  staleTime?: number;
};

export const requirementQueryKeys = createQueryKeys('requirement', {
  show: (requirementKey: MaybeRefOrGetter<string>, name: MaybeRefOrGetter<RequirementType>) => [{ requirementKey, name }],
  steps: (signatureRequestKey: MaybeRefOrGetter<string>) => ['steps', { signatureRequestKey }],
  finishStatus: (signatureRequestKey: MaybeRefOrGetter<string>) => ['finishRequirementsStatus', { signatureRequestKey }],
});

export function useFetchRequirement<T extends RequirementType>(
  name: MaybeRefOrGetter<T>,
  requirementKey: MaybeRefOrGetter<string>,
  options: UseFetchRequirementOptions = {},
) {
  const result = useQuery({
    queryKey: requirementQueryKeys.show(requirementKey, name).queryKey,
    queryFn: () => fetchRequirement(toValue(name), toValue(requirementKey)),
    staleTime: options.staleTime ?? 50,
    refetchOnWindowFocus: false,
  });

  return {
    ...result,
    requirement: result.data,
  };
}

export function useFetchRequirementSteps(signatureRequestKey: MaybeRefOrGetter<string>) {
  const result = useQuery({
    queryKey: [...requirementQueryKeys.steps(signatureRequestKey).queryKey],
    staleTime: minutesToMilliseconds(3),
    queryFn: async () => {
      const steps = await fetchRequirementSteps(toValue(signatureRequestKey));

      return { ...steps, next: undefined };
    },
  });

  return {
    ...result,
    steps: result.data,
  };
}

export function useFetchFinishRequirementsStatus(signatureRequestKey: MaybeRefOrGetter<string>) {
  const { data, ...result } = useQuery({
    queryKey: requirementQueryKeys.finishStatus(signatureRequestKey).queryKey,
    queryFn: () => fetchFinishRequirementsStatus(toValue(signatureRequestKey)),
    retry: false,
    initialDataUpdatedAt: 0,
    initialData: {
      documentsCount: 0,
      documentsSignedCount: 0,
    },
  });

  return {
    finishStatus: data,
    ...result,
  };
}
